import { Helmet } from "react-helmet-async";

/**
 * Centralized JSON-LD structured data for Answer Engine Optimization (AEO).
 * Injected via React Helmet so AI search engines (ChatGPT, Perplexity, Google AI,
 * Claude) can deeply understand the business, listings, and FAQs.
 */

const SITE_URL = "https://oonaflats.com";
const BRAND = "Oona Flats";
const ORG_LOGO = `${SITE_URL}/logo512.png`;
const CONTACT_EMAIL = "office@sharedspace-rentals.com";

// --- Organization (sitewide) ---
export function OrganizationSchema() {
  const data = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: BRAND,
    legalName: "SharedSpace GmbH",
    url: SITE_URL,
    logo: ORG_LOGO,
    description:
      "Oona Flats ist ein Premium-Buchungsportal für handverlesene Apartments in Wien. Alle Unterkünfte sind im Apple-Style designt und werden direkt von SharedSpace GmbH verwaltet.",
    address: {
      "@type": "PostalAddress",
      addressLocality: "Wien",
      addressCountry: "AT",
    },
    contactPoint: {
      "@type": "ContactPoint",
      email: CONTACT_EMAIL,
      contactType: "customer service",
      availableLanguage: ["de", "en"],
    },
    sameAs: [],
  };
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(data)}</script>
    </Helmet>
  );
}

// --- WebSite with SearchAction (helps AI engines surface internal search) ---
export function WebsiteSchema() {
  const data = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: BRAND,
    url: SITE_URL,
    inLanguage: ["de-AT", "en"],
    potentialAction: {
      "@type": "SearchAction",
      target: `${SITE_URL}/unterkuenfte?q={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  };
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(data)}</script>
    </Helmet>
  );
}

// --- LodgingBusiness for a single listing (Detail Page) ---
export function LodgingBusinessSchema({ listing }) {
  if (!listing) return null;
  const data = {
    "@context": "https://schema.org",
    "@type": "Apartment",
    name: listing.name,
    description: listing.description?.slice(0, 600),
    url: `${SITE_URL}/unterkunft/${listing.id}`,
    image: listing.images?.slice(0, 8) || [listing.thumbnailUrl].filter(Boolean),
    numberOfRooms: listing.bedroomsNumber,
    occupancy: {
      "@type": "QuantitativeValue",
      maxValue: listing.personCapacity,
      unitText: "guests",
    },
    floorSize: listing.squareMeters
      ? { "@type": "QuantitativeValue", value: listing.squareMeters, unitCode: "MTK" }
      : undefined,
    address: {
      "@type": "PostalAddress",
      name: `Wien${listing.district ? `, ${listing.district}` : ""} – genaue Adresse nach Buchung`,
      addressLocality: "Wien",
      addressRegion: listing.district || undefined,
      addressCountry: listing.country || "AT",
    },
    geo: listing.lat && listing.lng ? {
      "@type": "GeoCoordinates",
      latitude: Number(Number(listing.lat).toFixed(3)),
      longitude: Number(Number(listing.lng).toFixed(3)),
    } : undefined,
    offers: {
      "@type": "Offer",
      price: listing.price,
      priceCurrency: "EUR",
      availability: "https://schema.org/InStock",
      url: `${SITE_URL}/unterkunft/${listing.id}`,
    },
    brand: { "@type": "Brand", name: BRAND },
  };
  // remove undefined keys
  Object.keys(data).forEach((k) => data[k] === undefined && delete data[k]);
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(data)}</script>
    </Helmet>
  );
}

// --- FAQ Schema (HomePage) — direct answers AI engines surface verbatim ---
export const FAQ_ITEMS = [
  {
    q: "Wie kann ich ein Apartment bei Oona Flats buchen?",
    a: "Wählen Sie auf oonaflats.com ein Apartment, geben Sie Ihre Reisedaten ein und schließen Sie die Buchung sicher per Kreditkarte über Stripe ab. Sie erhalten umgehend eine Buchungsbestätigung per E-Mail. Eine Anmeldung ist nicht erforderlich.",
  },
  {
    q: "Wo befinden sich die Apartments von Oona Flats?",
    a: "Alle Apartments von Oona Flats befinden sich in zentraler Lage in Wien, Österreich. Die genaue Adresse erhalten Sie nach erfolgreicher Buchung per E-Mail.",
  },
  {
    q: "Welche Zahlungsmethoden werden akzeptiert?",
    a: "Oona Flats akzeptiert alle gängigen Kredit- und Debitkarten (Visa, Mastercard, American Express) sicher über Stripe. Die Zahlung erfolgt direkt online beim Buchungsabschluss.",
  },
  {
    q: "Kann ich meine Buchung stornieren?",
    a: "Stornierungsbedingungen sind je Apartment unterschiedlich und werden Ihnen vor dem Buchungsabschluss transparent angezeigt. Bei Fragen erreichen Sie uns unter office@sharedspace-rentals.com.",
  },
  {
    q: "Sind die Apartments für Familien geeignet?",
    a: "Ja, viele Apartments sind familienfreundlich und bieten bis zu 4 Schlafzimmer. Filtern Sie auf der Unterkünfte-Seite nach Gästekapazität, um das passende Apartment zu finden.",
  },
  {
    q: "Wann erfolgt der Check-in und Check-out?",
    a: "Check-in ist in der Regel ab 15:00 Uhr und Check-out bis 11:00 Uhr. Self-Check-in ist möglich – die genauen Anweisungen erhalten Sie 24 Stunden vor Anreise per E-Mail.",
  },
  {
    q: "Bietet Oona Flats Apartments mit WLAN, Küche und Waschmaschine?",
    a: "Ja, alle Apartments verfügen über kostenfreies Highspeed-WLAN, eine voll ausgestattete Küche und in den meisten Fällen auch Waschmaschine. Details finden Sie in der Ausstattungsliste jedes Apartments.",
  },
  {
    q: "Wer betreibt Oona Flats?",
    a: "Oona Flats wird von SharedSpace GmbH mit Sitz in Wien, Österreich betrieben. Vollständige Impressumsangaben finden Sie unter oonaflats.com/impressum.",
  },
];

export function FAQSchema() {
  const data = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ_ITEMS.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.a,
      },
    })),
  };
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(data)}</script>
    </Helmet>
  );
}

// --- BreadcrumbList (per page) ---
export function BreadcrumbSchema({ items = [] }) {
  if (!items.length) return null;
  const data = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: `${SITE_URL}${item.path}`,
    })),
  };
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(data)}</script>
    </Helmet>
  );
}
